import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  ArrowLeft,
  Download,
  FileJson,
  FileSpreadsheet,
  Trash2,
  RefreshCw,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  Eye,
  EyeOff,
  Image,
  File,
  CreditCard,
  Banknote,
  Building2,
  Hash,
  Code,
  FileCode,
} from "lucide-react";
import "./COBWorkOrderDetail.css";

// API functions for COB
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// Extract work order APIs
const cobGetWorkOrder = async (workOrderId) => {
  const response = await fetch(`/cob/api/work-orders/${workOrderId}`, { 
    headers: getAuthHeaders() 
  });
  if (!response.ok) throw new Error('Failed to fetch work order');
  return response.json();
};

const cobDeleteWorkOrder = async (workOrderId) => {
  const response = await fetch(`/cob/api/work-orders/${workOrderId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to delete work order');
  return response.json();
};

const cobGetWorkOrderFileUrl = (workOrderId) => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return `/cob/api/work-orders/${workOrderId}/file?token=${token}`;
};

const cobGetWorkOrderPreviewUrl = (workOrderId) => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return `/cob/api/work-orders/${workOrderId}/preview?token=${token}`;
};

// Process work order APIs
const cobGetProcessWorkOrder = async (workOrderId, version = 'v2') => {
  const response = await fetch(`/cob/api/${version}/process/work-orders/${workOrderId}`, { 
    headers: getAuthHeaders() 
  });
  if (!response.ok) throw new Error('Failed to fetch work order');
  return response.json();
};

const cobDeleteProcessWorkOrder = async (workOrderId, version = 'v2') => {
  const response = await fetch(`/cob/api/${version}/process/work-orders/${workOrderId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to delete work order');
  return response.json();
};

const cobGetProcessWorkOrderFileUrl = (workOrderId, version = 'v2') => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return `/cob/api/${version}/process/work-orders/${workOrderId}/file?token=${token}`;
};

const cobGetProcessWorkOrderPreviewUrl = (workOrderId, version = 'v2') => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return `/cob/api/${version}/process/work-orders/${workOrderId}/preview?token=${token}`;
};

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = true, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="cob-collapsible-section">
      <button className="cob-section-header" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        {Icon && <Icon size={18} className="section-icon" />}
        <span>{title}</span>
      </button>
      {isOpen && <div className="cob-section-content">{children}</div>}
    </div>
  );
};

// Cheque Card Component
const ChequeCard = ({ cheque, index, onCopy, copiedField }) => {
  return (
    <div className="cob-cheque-card">
      <div className="cob-cheque-header">
        <div className="cob-cheque-icon">
          <CreditCard size={20} />
        </div>
        <div className="cob-cheque-title">
          <h4>Cheque {index + 1}</h4>
          {cheque.page_number && (
            <span className="cob-cheque-page">Page {cheque.page_number}</span>
          )}
        </div>
        {cheque.confidence && (
          <div className={`cob-confidence-badge ${cheque.confidence > 0.8 ? 'high' : cheque.confidence > 0.5 ? 'medium' : 'low'}`}>
            {Math.round(cheque.confidence * 100)}%
          </div>
        )}
      </div>
      
      <div className="cob-cheque-details">
        {/* Bank Name */}
        <div className="cob-cheque-field">
          <div className="cob-field-icon">
            <Building2 size={16} />
          </div>
          <div className="cob-field-content">
            <span className="cob-field-label">Bank Name</span>
            <span className="cob-field-value">{cheque.bank_name || "-"}</span>
          </div>
          <button 
            className="cob-copy-btn"
            onClick={() => onCopy(cheque.bank_name || "", `cheque-${index}-bank`)}
          >
            <Copy size={14} />
            {copiedField === `cheque-${index}-bank` && <span className="copied-tooltip">Copied!</span>}
          </button>
        </div>

        {/* Account Number */}
        <div className="cob-cheque-field">
          <div className="cob-field-icon">
            <Hash size={16} />
          </div>
          <div className="cob-field-content">
            <span className="cob-field-label">Account Number</span>
            <span className="cob-field-value monospace">{cheque.account_number || "-"}</span>
          </div>
          <button 
            className="cob-copy-btn"
            onClick={() => onCopy(cheque.account_number || "", `cheque-${index}-account`)}
          >
            <Copy size={14} />
            {copiedField === `cheque-${index}-account` && <span className="copied-tooltip">Copied!</span>}
          </button>
        </div>

        {/* IFSC Code */}
        <div className="cob-cheque-field">
          <div className="cob-field-icon">
            <Banknote size={16} />
          </div>
          <div className="cob-field-content">
            <span className="cob-field-label">IFSC Code</span>
            <span className="cob-field-value monospace">{cheque.ifsc_code || "-"}</span>
          </div>
          <button 
            className="cob-copy-btn"
            onClick={() => onCopy(cheque.ifsc_code || "", `cheque-${index}-ifsc`)}
          >
            <Copy size={14} />
            {copiedField === `cheque-${index}-ifsc` && <span className="copied-tooltip">Copied!</span>}
          </button>
        </div>

        {/* MICR Code */}
        <div className="cob-cheque-field">
          <div className="cob-field-icon">
            <CreditCard size={16} />
          </div>
          <div className="cob-field-content">
            <span className="cob-field-label">MICR Code</span>
            <span className="cob-field-value monospace">{cheque.micr_code || "-"}</span>
          </div>
          <button 
            className="cob-copy-btn"
            onClick={() => onCopy(cheque.micr_code || "", `cheque-${index}-micr`)}
          >
            <Copy size={14} />
            {copiedField === `cheque-${index}-micr` && <span className="copied-tooltip">Copied!</span>}
          </button>
        </div>

        {/* Account Holder Name (if available) */}
        {cheque.account_holder_name && (
          <div className="cob-cheque-field">
            <div className="cob-field-icon">
              <FileText size={16} />
            </div>
            <div className="cob-field-content">
              <span className="cob-field-label">Account Holder</span>
              <span className="cob-field-value">{cheque.account_holder_name}</span>
            </div>
            <button 
              className="cob-copy-btn"
              onClick={() => onCopy(cheque.account_holder_name, `cheque-${index}-holder`)}
            >
              <Copy size={14} />
              {copiedField === `cheque-${index}-holder` && <span className="copied-tooltip">Copied!</span>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Form Bank Details Card Component
const FormBankDetailsCard = ({ details, type, onCopy, copiedField }) => {
  if (!details) return null;
  
  return (
    <div className={`cob-bank-details-card ${type}`}>
      <div className="cob-bank-card-header">
        <Building2 size={20} />
        <h4>{type === 'old' ? 'Old Bank Details' : 'New Bank Details'}</h4>
      </div>
      <div className="cob-bank-fields">
        {Object.entries(details).map(([key, value]) => (
          <div key={key} className="cob-bank-field">
            <span className="cob-bank-field-label">
              {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
            <span className="cob-bank-field-value">{value || "-"}</span>
            <button 
              className="cob-copy-btn"
              onClick={() => onCopy(value || "", `${type}-${key}`)}
            >
              <Copy size={12} />
              {copiedField === `${type}-${key}` && <span className="copied-tooltip">Copied!</span>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Recursive JSON Renderer
const JsonRenderer = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) {
    return <span className="cob-json-null">null</span>;
  }

  if (typeof data === "boolean") {
    return <span className="cob-json-boolean">{data.toString()}</span>;
  }

  if (typeof data === "number") {
    return <span className="cob-json-number">{data}</span>;
  }

  if (typeof data === "string") {
    return <span className="cob-json-string">"{data}"</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="cob-json-empty">[]</span>;
    return (
      <div className="cob-json-array">
        {data.map((item, index) => (
          <div key={index} className="cob-json-array-item" style={{ marginLeft: depth * 16 }}>
            <span className="cob-json-index">[{index}]</span>
            <JsonRenderer data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (entries.length === 0) return <span className="cob-json-empty">{"{}"}</span>;
    return (
      <div className="cob-json-object">
        {entries.map(([key, value]) => (
          <div key={key} className="cob-json-property" style={{ marginLeft: depth * 16 }}>
            <span className="cob-json-key">{key}:</span>
            <JsonRenderer data={value} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(data)}</span>;
};

// Multi-Page TIFF Viewer Component - Scrollable like PDF viewer
const MultiPageTiffViewer = ({ workOrderId, totalPages, filename, onError, isProcessWorkOrder = false }) => {
  const [loadedPages, setLoadedPages] = useState({});
  const [loadErrors, setLoadErrors] = useState({});
  const [allFailed, setAllFailed] = useState(false);
  
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  
  const getPageUrl = (pageNum) => {
    // Use process or extract endpoint based on work order type
    const basePath = isProcessWorkOrder 
      ? `/cob/api/v2/process/work-orders/${workOrderId}/preview`
      : `/cob/api/work-orders/${workOrderId}/preview`;
    return `${basePath}?token=${token}&page=${pageNum}`;
  };

  const handlePageLoad = (pageNum) => {
    setLoadedPages(prev => ({ ...prev, [pageNum]: true }));
  };

  const handlePageError = (pageNum) => {
    setLoadErrors(prev => {
      const newErrors = { ...prev, [pageNum]: true };
      // If first page fails, trigger the parent error handler
      if (pageNum === 0 && onError) {
        setAllFailed(true);
        onError();
      }
      return newErrors;
    });
  };

  const pageCount = totalPages || 1;

  // If all pages failed (starting from first), don't render anything
  if (allFailed) {
    return null;
  }

  return (
    <div className="cob-tiff-scroll-viewer">
      {/* All pages stacked vertically in a scrollable container */}
      {Array.from({ length: pageCount }, (_, i) => (
        <div key={i} className="cob-tiff-page">
          {loadErrors[i] ? (
            <div className="cob-tiff-page-error">
              <AlertCircle size={20} />
              <span>Page {i + 1} failed to load</span>
            </div>
          ) : (
            <img
              src={getPageUrl(i)}
              alt={`${filename || 'Document'} - Page ${i + 1}`}
              className="cob-tiff-page-image"
              onLoad={() => handlePageLoad(i)}
              onError={() => handlePageError(i)}
              loading={i === 0 ? "eager" : "lazy"}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// XML Viewer Component with syntax highlighting
const XmlViewer = ({ xml, onCopy, copiedField }) => {
  if (!xml) {
    return (
      <div className="cob-no-data">
        <FileCode size={48} />
        <p>No XML data available</p>
        <span>XML extraction data will appear here for new extractions</span>
      </div>
    );
  }

  // Format XML with indentation
  const formatXml = (xmlStr) => {
    try {
      let formatted = '';
      let indent = 0;
      const tab = '  ';
      
      xmlStr.split(/>\s*</).forEach(node => {
        if (node.match(/^\/\w/)) indent--;
        formatted += tab.repeat(Math.max(0, indent)) + '<' + node + '>\n';
        if (node.match(/^<?\w[^>]*[^\/]$/) && !node.startsWith('?')) indent++;
      });
      
      return formatted.substring(1, formatted.length - 2);
    } catch (e) {
      return xmlStr;
    }
  };

  const formattedXml = formatXml(xml);

  // Parse XML to extract key fields for display
  const parseXmlFields = (xmlStr) => {
    const fields = {};
    const regex = /<(\w+)>([^<]*)<\/\1>/g;
    let match;
    while ((match = regex.exec(xmlStr)) !== null) {
      if (match[2] && match[2].trim()) {
        fields[match[1]] = match[2].trim();
      }
    }
    return fields;
  };

  const fields = parseXmlFields(xml);

  return (
    <div className="cob-xml-viewer">
      <div className="cob-xml-header">
        <h4>
          <FileCode size={18} />
          Extracted XML Data
        </h4>
        <div className="cob-xml-actions">
          <button
            className="cob-btn-small"
            onClick={() => {
              const blob = new Blob([xml], { type: 'application/xml' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'extraction.xml';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <Download size={14} /> Download XML
          </button>
          <button
            className="cob-btn-small"
            onClick={() => onCopy(xml, 'xml')}
          >
            <Copy size={14} /> Copy
            {copiedField === 'xml' && <span className="copied-tooltip">Copied!</span>}
          </button>
        </div>
      </div>

      {/* Key Fields Summary */}
      {Object.keys(fields).length > 0 && (
        <div className="cob-xml-fields-summary">
          <h5>Key Extracted Fields ({Object.keys(fields).length})</h5>
          <div className="cob-xml-fields-grid">
            {Object.entries(fields).slice(0, 20).map(([key, value]) => (
              <div key={key} className="cob-xml-field">
                <span className="cob-xml-field-name">{key}</span>
                <span className="cob-xml-field-value">{value}</span>
                <button
                  className="cob-copy-btn-mini"
                  onClick={() => onCopy(value, `field-${key}`)}
                >
                  <Copy size={12} />
                </button>
              </div>
            ))}
            {Object.keys(fields).length > 20 && (
              <div className="cob-xml-more">+ {Object.keys(fields).length - 20} more fields</div>
            )}
          </div>
        </div>
      )}

      {/* Raw XML */}
      <div className="cob-xml-raw">
        <h5>Raw XML</h5>
        <pre className="cob-xml-code">{formattedXml}</pre>
      </div>
    </div>
  );
};

// Section Results Viewer Component
const SectionResultsViewer = ({ sectionResults, onCopy, copiedField }) => {
  const [expandedSections, setExpandedSections] = useState(new Set(['distributor', 'applicant1']));

  if (!sectionResults || Object.keys(sectionResults).length === 0) {
    return (
      <div className="cob-no-data">
        <FileText size={48} />
        <p>No section results available</p>
        <span>Section-by-section extraction results will appear here</span>
      </div>
    );
  }

  const toggleSection = (sectionName) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionName)) {
      newExpanded.delete(sectionName);
    } else {
      newExpanded.add(sectionName);
    }
    setExpandedSections(newExpanded);
  };

  return (
    <div className="cob-section-results">
      <h4>
        <FileText size={18} />
        Section-by-Section Results ({Object.keys(sectionResults).length} sections)
      </h4>
      <div className="cob-sections-list">
        {Object.entries(sectionResults).map(([sectionName, sectionData]) => {
          const isExpanded = expandedSections.has(sectionName);
          const fields = Object.entries(sectionData || {}).filter(([k]) => !k.startsWith('_'));
          const confidence = sectionData?._confidence;
          
          return (
            <div key={sectionName} className="cob-section-item">
              <button 
                className="cob-section-toggle"
                onClick={() => toggleSection(sectionName)}
              >
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span className="cob-section-name">{sectionName.replace(/_/g, ' ')}</span>
                <span className="cob-section-count">{fields.length} fields</span>
              </button>
              {isExpanded && (
                <div className="cob-section-fields">
                  {fields.length > 0 ? (
                    fields.map(([fieldName, fieldValue]) => (
                      <div key={fieldName} className="cob-section-field">
                        <span className="cob-section-field-name">{fieldName}</span>
                        <span className="cob-section-field-value">
                          {typeof fieldValue === 'object' ? JSON.stringify(fieldValue) : String(fieldValue || '')}
                        </span>
                        {confidence && confidence[fieldName] !== undefined && (
                          <span className={`cob-confidence-mini ${confidence[fieldName] >= 80 ? 'high' : confidence[fieldName] >= 50 ? 'medium' : 'low'}`}>
                            {confidence[fieldName]}%
                          </span>
                        )}
                        <button
                          className="cob-copy-btn-mini"
                          onClick={() => onCopy(String(fieldValue || ''), `section-${sectionName}-${fieldName}`)}
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="cob-section-empty">No fields extracted</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const COBWorkOrderDetail = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detect if this is a process work order from URL path
  const isProcessWorkOrder = location.pathname.includes('/process-work-order/');

  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("xml");
  const [copiedField, setCopiedField] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(true);
  const [fileFullscreen, setFileFullscreen] = useState(false);
  const [fileLoadError, setFileLoadError] = useState(false);

  // Check if this is a v3 (Change Of Bank) work order
  const isV3WorkOrder = workOrder?.version === "v3";

  // Use process or extract API URLs based on work order type
  const fileUrl = workOrderId 
    ? (isProcessWorkOrder ? cobGetProcessWorkOrderFileUrl(workOrderId) : cobGetWorkOrderFileUrl(workOrderId))
    : null;
  const previewUrl = workOrderId 
    ? (isProcessWorkOrder ? cobGetProcessWorkOrderPreviewUrl(workOrderId) : cobGetWorkOrderPreviewUrl(workOrderId))
    : null;

  useEffect(() => {
    if (workOrderId) {
      fetchWorkOrder();
    }
  }, [workOrderId, isProcessWorkOrder]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      setError("");
      // Use process or extract API based on URL path
      const data = isProcessWorkOrder 
        ? await cobGetProcessWorkOrder(workOrderId)
        : await cobGetWorkOrder(workOrderId);
      setWorkOrder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this work order? This cannot be undone.")) {
      return;
    }

    try {
      if (isProcessWorkOrder) {
        await cobDeleteProcessWorkOrder(workOrderId);
        navigate("/cob/v2-process-workorders");
      } else {
        await cobDeleteWorkOrder(workOrderId);
        // Navigate to appropriate list based on version
        if (isV3WorkOrder) {
          navigate("/cob/v3-workorders");
        } else {
          navigate("/cob/history");
        }
      }
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleDownloadJSON = () => {
    if (!workOrder) return;

    const data = {
      cheques: workOrder.cheques || [],
      form_bank_details: workOrder.form_bank_details || null,
      metadata: {
        work_order_id: workOrder._id,
        filename: workOrder.filename,
        total_pages: workOrder.total_pages,
        cheques_found: workOrder.cheques_found,
        processing_time: workOrder.processing_time,
      }
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(/\.[^/.]+$/, "") || workOrderId}_cheques.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = () => {
    if (!workOrder?.cheques?.length) return;

    // CSV for cheques
    const headers = ['cheque_index', 'page_number', 'bank_name', 'account_number', 'ifsc_code', 'micr_code', 'account_holder_name', 'confidence'];
    const rows = workOrder.cheques.map((cheque, idx) => [
      idx + 1,
      cheque.page_number || '',
      cheque.bank_name || '',
      cheque.account_number || '',
      cheque.ifsc_code || '',
      cheque.micr_code || '',
      cheque.account_holder_name || '',
      cheque.confidence || ''
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));

    const csv = [headers.join(','), ...rows].join('\n');

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workOrder.filename?.replace(/\.[^/.]+$/, "") || workOrderId}_cheques.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = useCallback((text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={20} className="status-icon completed" />;
      case "failed":
        return <XCircle size={20} className="status-icon failed" />;
      case "processing":
        return <RefreshCw size={20} className="status-icon processing spin" />;
      default:
        return <AlertCircle size={20} className="status-icon pending" />;
    }
  };

  const getFileExtension = (filename) => {
    if (!filename) return 'pdf';
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext || 'pdf';
  };

  const isImageFile = (filename) => {
    const ext = getFileExtension(filename);
    return ['tiff', 'tif', 'jpg', 'jpeg', 'png', 'gif'].includes(ext);
  };

  if (loading) {
    return (
      <div className="cob-detail-loading">
        <RefreshCw size={32} className="spin" />
        <p>Loading work order...</p>
      </div>
    );
  }

  if (error && !workOrder) {
    return (
      <div className="cob-detail-error">
        <XCircle size={48} />
        <h3>Error Loading Work Order</h3>
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="cob-detail-error">
        <AlertCircle size={48} />
        <h3>Work Order Not Found</h3>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const cheques = workOrder.cheques || [];
  const formBankDetails = workOrder.form_bank_details || null;

  return (
    <div className="cob-work-order-detail">
      {/* Back Button */}
      <button className="cob-back-button" onClick={() => {
        if (isV3WorkOrder) {
          navigate("/cob/v3-workorders");
        } else if (isProcessWorkOrder) {
          navigate("/cob/v2-process-workorders");
        } else {
          navigate("/cob/history");
        }
      }}>
        <ArrowLeft size={18} />
        Back to Work Orders
      </button>

      {/* Header */}
      <div className="cob-detail-header">
        <div className="cob-detail-header-left">
          <div className="cob-detail-title">
            <h1>
              <Banknote size={24} />
              {workOrder.filename || "Work Order"}
            </h1>
            <span className={`cob-pipeline-tag ${isV3WorkOrder ? 'v3' : ''}`} style={isV3WorkOrder ? { backgroundColor: '#7c3aed' } : {}}>
              {isV3WorkOrder ? 'Change Of Bank' : 'COB'}
            </span>
          </div>
          <div className="cob-detail-meta">
            <span className="cob-meta-item">
              <strong>ID:</strong> {workOrder._id}
              <button
                className="cob-copy-btn"
                onClick={() => copyToClipboard(workOrder._id, "id")}
                title="Copy ID"
              >
                <Copy size={14} />
                {copiedField === "id" && <span className="copied-tooltip">Copied!</span>}
              </button>
            </span>
            {workOrder.uid && (
              <span className="cob-meta-item">
                <strong>UID:</strong> {workOrder.uid}
              </span>
            )}
          </div>
        </div>

        <div className="cob-detail-header-right">
          <div className={`cob-status-badge status-${workOrder.status}`}>
            {getStatusIcon(workOrder.status)}
            {workOrder.status}
          </div>
        </div>
      </div>

      {error && (
        <div className="cob-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Info Cards */}
      <div className="cob-info-cards">
        <div className="cob-info-card">
          <Clock size={20} />
          <div>
            <span className="cob-info-label">Created</span>
            <span className="cob-info-value">{formatDate(workOrder.created_at)}</span>
          </div>
        </div>
        <div className="cob-info-card">
          <FileText size={20} />
          <div>
            <span className="cob-info-label">Pages</span>
            <span className="cob-info-value">{workOrder.total_pages || "-"}</span>
          </div>
        </div>
        <div className="cob-info-card highlight">
          <CreditCard size={20} />
          <div>
            <span className="cob-info-label">Cheques Found</span>
            <span className="cob-info-value">{workOrder.cheques_found || cheques.length || 0}</span>
          </div>
        </div>
        <div className="cob-info-card">
          <RefreshCw size={20} />
          <div>
            <span className="cob-info-label">Processing Time</span>
            <span className="cob-info-value">
              {workOrder.processing_time ? `${parseFloat(workOrder.processing_time).toFixed(2)}s` : "-"}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="cob-action-bar">
        <button 
          className={`cob-action-btn toggle ${showFileViewer ? 'active' : ''}`}
          onClick={() => setShowFileViewer(!showFileViewer)}
          title={showFileViewer ? "Hide File Viewer" : "Show File Viewer"}
        >
          {showFileViewer ? <EyeOff size={16} /> : <Eye size={16} />}
          {showFileViewer ? "Hide File" : "Show File"}
        </button>
        <button className="cob-action-btn primary" onClick={handleDownloadJSON}>
          <FileJson size={16} />
          JSON
        </button>
        <button className="cob-action-btn secondary" onClick={handleDownloadCSV} disabled={!cheques.length}>
          <FileSpreadsheet size={16} />
          CSV
        </button>
        <button className="cob-action-btn refresh" onClick={fetchWorkOrder}>
          <RefreshCw size={16} />
          Refresh
        </button>
        <button className="cob-action-btn danger" onClick={handleDelete}>
          <Trash2 size={16} />
          Delete
        </button>
      </div>

      {/* Split View Container */}
      <div className={`cob-split-container ${showFileViewer ? 'with-file' : 'no-file'} ${fileFullscreen ? 'file-fullscreen' : ''}`}>
        {/* File Viewer Panel */}
        {showFileViewer && (
          <div className="cob-file-panel">
            <div className="cob-file-header">
              <h3>
                {isImageFile(workOrder.filename) ? <Image size={18} /> : <FileText size={18} />}
                {workOrder.filename || 'Document'}
              </h3>
              <div className="cob-file-controls">
                <span className="cob-file-type-badge">
                  {getFileExtension(workOrder.filename).toUpperCase()}
                </span>
                <button 
                  className="cob-file-control-btn"
                  onClick={() => setFileFullscreen(!fileFullscreen)}
                  title={fileFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                >
                  {fileFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <a 
                  href={fileUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="cob-file-control-btn"
                  title="Download Original"
                >
                  <Download size={16} />
                </a>
              </div>
            </div>
            <div className="cob-file-viewer">
              {workOrderId && !fileLoadError ? (
                <iframe
                  src={previewUrl}
                  title="Document Viewer"
                  className="cob-file-iframe"
                  onError={() => setFileLoadError(true)}
                />
              ) : (
                <div className="cob-file-unavailable">
                  <File size={48} />
                  <p>{fileLoadError ? "Unable to display this file" : "File not available"}</p>
                  <a href={fileUrl} download className="cob-download-link">
                    <Download size={16} /> Download Original File
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Panel */}
        <div className="cob-data-panel">
          {/* Tabs */}
          <div className="cob-tabs">
            {/* XML Tab - For all versions */}
            <button
              className={`cob-tab ${activeTab === "xml" ? "active" : ""}`}
              onClick={() => setActiveTab("xml")}
            >
              <FileCode size={16} />
              XML Data
            </button>
            
            {/* V3 Change Of Bank specific tabs */}
            {isV3WorkOrder && (
              <>
                <button
                  className={`cob-tab ${activeTab === "cheques" ? "active" : ""}`}
                  onClick={() => setActiveTab("cheques")}
                >
                  <CreditCard size={16} />
                  Cheques ({cheques.length})
                </button>
                <button
                  className={`cob-tab ${activeTab === "bankdetails" ? "active" : ""}`}
                  onClick={() => setActiveTab("bankdetails")}
                >
                  <Building2 size={16} />
                  Bank Details
                </button>
              </>
            )}
            
            {/* V1/V2 Subscription Checker specific tabs */}
            {!isV3WorkOrder && (
              <>
                <button
                  className={`cob-tab ${activeTab === "sections" ? "active" : ""}`}
                  onClick={() => setActiveTab("sections")}
                >
                  <FileText size={16} />
                  Sections
                </button>
                <button
                  className={`cob-tab ${activeTab === "cheques" ? "active" : ""}`}
                  onClick={() => setActiveTab("cheques")}
                >
                  <CreditCard size={16} />
                  Cheques ({cheques.length})
                </button>
                <button
                  className={`cob-tab ${activeTab === "form" ? "active" : ""}`}
                  onClick={() => setActiveTab("form")}
                >
                  <Building2 size={16} />
                  Form Bank Details
                </button>
              </>
            )}
            
            {/* Common tabs for all versions */}
            <button
              className={`cob-tab ${activeTab === "raw" ? "active" : ""}`}
              onClick={() => setActiveTab("raw")}
            >
              Raw JSON
            </button>
            <button
              className={`cob-tab ${activeTab === "metadata" ? "active" : ""}`}
              onClick={() => setActiveTab("metadata")}
            >
              Metadata
            </button>
          </div>

          {/* Tab Content */}
          <div className="cob-tab-content">
            {/* XML Tab - New extraction format */}
            {activeTab === "xml" && (
              <XmlViewer 
                xml={workOrder.merged_xml || workOrder.xml || workOrder.extraction_data?.xml} 
                onCopy={copyToClipboard}
                copiedField={copiedField}
              />
            )}

            {/* Sections Tab - V1/V2 Subscription Checker only */}
            {activeTab === "sections" && !isV3WorkOrder && (
              <SectionResultsViewer
                sectionResults={workOrder.section_results || workOrder.extraction_data?.section_results}
                onCopy={copyToClipboard}
                copiedField={copiedField}
              />
            )}

            {/* Cheques Tab - All versions */}
            {activeTab === "cheques" && (
              <div className="cob-cheques-tab">
                {cheques.length > 0 ? (
                  <div className="cob-cheques-grid">
                    {cheques.map((cheque, index) => (
                      <ChequeCard 
                        key={index} 
                        cheque={cheque} 
                        index={index} 
                        onCopy={copyToClipboard}
                        copiedField={copiedField}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="cob-no-data">
                    <CreditCard size={48} />
                    <p>No cheques extracted</p>
                    <span>{isV3WorkOrder ? "Cheque details from the Change of Bank form will appear here" : "Legacy cheque extraction data will appear here"}</span>
                  </div>
                )}
              </div>
            )}

            {/* V3 Bank Details Tab - Change Of Bank */}
            {activeTab === "bankdetails" && isV3WorkOrder && (
              <div className="cob-form-tab">
                {formBankDetails ? (
                  <div className="cob-form-details-grid">
                    <FormBankDetailsCard 
                      details={formBankDetails.old_bank} 
                      type="old" 
                      onCopy={copyToClipboard}
                      copiedField={copiedField}
                    />
                    <FormBankDetailsCard 
                      details={formBankDetails.new_bank} 
                      type="new" 
                      onCopy={copyToClipboard}
                      copiedField={copiedField}
                    />
                    {/* Show additional form-level details if available */}
                    {(formBankDetails.folio_number || formBankDetails.applicant_name || formBankDetails.pan) && (
                      <div className="cob-bank-details-card applicant">
                        <div className="cob-bank-card-header">
                          <FileText size={20} />
                          <h4>Applicant Details</h4>
                        </div>
                        <div className="cob-bank-fields">
                          {formBankDetails.folio_number && (
                            <div className="cob-bank-field">
                              <span className="cob-bank-field-label">Folio Number</span>
                              <span className="cob-bank-field-value">{formBankDetails.folio_number}</span>
                              <button 
                                className="cob-copy-btn"
                                onClick={() => copyToClipboard(formBankDetails.folio_number, "folio")}
                              >
                                <Copy size={12} />
                                {copiedField === "folio" && <span className="copied-tooltip">Copied!</span>}
                              </button>
                            </div>
                          )}
                          {formBankDetails.applicant_name && (
                            <div className="cob-bank-field">
                              <span className="cob-bank-field-label">Applicant Name</span>
                              <span className="cob-bank-field-value">{formBankDetails.applicant_name}</span>
                              <button 
                                className="cob-copy-btn"
                                onClick={() => copyToClipboard(formBankDetails.applicant_name, "applicant")}
                              >
                                <Copy size={12} />
                                {copiedField === "applicant" && <span className="copied-tooltip">Copied!</span>}
                              </button>
                            </div>
                          )}
                          {formBankDetails.pan && (
                            <div className="cob-bank-field">
                              <span className="cob-bank-field-label">PAN</span>
                              <span className="cob-bank-field-value">{formBankDetails.pan}</span>
                              <button 
                                className="cob-copy-btn"
                                onClick={() => copyToClipboard(formBankDetails.pan, "pan")}
                              >
                                <Copy size={12} />
                                {copiedField === "pan" && <span className="copied-tooltip">Copied!</span>}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="cob-no-data">
                    <Building2 size={48} />
                    <p>No bank details extracted</p>
                    <span>Old and New bank details from the Change of Bank form will appear here</span>
                  </div>
                )}
              </div>
            )}

            {/* V1/V2 Form Bank Details Tab - Legacy */}
            {activeTab === "form" && !isV3WorkOrder && (
              <div className="cob-form-tab">
                {formBankDetails ? (
                  <div className="cob-form-details-grid">
                    <FormBankDetailsCard 
                      details={formBankDetails.old_bank} 
                      type="old" 
                      onCopy={copyToClipboard}
                      copiedField={copiedField}
                    />
                    <FormBankDetailsCard 
                      details={formBankDetails.new_bank} 
                      type="new" 
                      onCopy={copyToClipboard}
                      copiedField={copiedField}
                    />
                  </div>
                ) : (
                  <div className="cob-no-data">
                    <Building2 size={48} />
                    <p>No form bank details extracted</p>
                    <span>Legacy bank details from the form will appear here</span>
                  </div>
                )}
              </div>
            )}

            {activeTab === "raw" && (
              <div className="cob-raw-json">
                <div className="cob-raw-json-header">
                  <button onClick={() => {
                    // Build the API response format
                    const apiResponse = {
                      merged_xml: workOrder.xml || workOrder.extraction_data?.xml || "",
                      validation: {
                        fail: workOrder.validation?.fail || [],
                        Score: workOrder.validation?.Score || workOrder.llm_confidence ? 
                          Object.entries(workOrder.llm_confidence || {}).map(([field, score]) => ({
                            field,
                            score: typeof score === 'number' ? score : parseInt(score) || 0,
                            htmlId: ""
                          })) : []
                      },
                      work_order: {
                        id: workOrder._id,
                        uid: workOrder.uid || "",
                        filename: workOrder.filename || "",
                        status: workOrder.status
                      },
                      meta_data: {
                        success: workOrder.status === "completed",
                        version: workOrder.version || "v2",
                        total_pages: workOrder.total_pages || 0,
                        processing_time_seconds: workOrder.processing_time || 0,
                        model: workOrder.model || "qwen",
                        engine: workOrder.version === "v1" ? "gemini" : "qwen",
                        page_roles: workOrder.page_roles || workOrder.extraction_data?.page_roles || {},
                        page_sizes: workOrder.page_sizes || workOrder.extraction_data?.page_sizes || []
                      }
                    };
                    const blob = new Blob([JSON.stringify(apiResponse, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${workOrder.filename?.replace(/\.[^/.]+$/, "") || workOrder._id}_response.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}>
                    <Download size={16} /> Download
                  </button>
                  <button
                    onClick={() => {
                      const apiResponse = {
                        merged_xml: workOrder.xml || workOrder.extraction_data?.xml || "",
                        validation: {
                          fail: workOrder.validation?.fail || [],
                          Score: workOrder.validation?.Score || []
                        },
                        work_order: {
                          id: workOrder._id,
                          uid: workOrder.uid || "",
                          filename: workOrder.filename || "",
                          status: workOrder.status
                        },
                        meta_data: {
                          success: workOrder.status === "completed",
                          version: workOrder.version || "v2",
                          total_pages: workOrder.total_pages || 0,
                          processing_time_seconds: workOrder.processing_time || 0,
                          model: workOrder.model || "qwen",
                          engine: workOrder.version === "v1" ? "gemini" : "qwen",
                          page_roles: workOrder.page_roles || workOrder.extraction_data?.page_roles || {},
                          page_sizes: workOrder.page_sizes || workOrder.extraction_data?.page_sizes || []
                        }
                      };
                      copyToClipboard(JSON.stringify(apiResponse, null, 2), "raw");
                    }}
                  >
                    <Copy size={16} /> Copy
                    {copiedField === "raw" && <span className="copied-tooltip">Copied!</span>}
                  </button>
                </div>
                <pre className="cob-json-viewer">
                  <JsonRenderer data={{
                    merged_xml: workOrder.xml || workOrder.extraction_data?.xml || "(No XML data)",
                    validation: {
                      fail: workOrder.validation?.fail || [],
                      Score: workOrder.validation?.Score || []
                    },
                    work_order: {
                      id: workOrder._id,
                      uid: workOrder.uid || "",
                      filename: workOrder.filename || "",
                      status: workOrder.status
                    },
                    meta_data: {
                      success: workOrder.status === "completed",
                      version: workOrder.version || "v2",
                      total_pages: workOrder.total_pages || 0,
                      processing_time_seconds: workOrder.processing_time || 0,
                      model: workOrder.model || "qwen",
                      engine: workOrder.version === "v1" ? "gemini" : "qwen",
                      page_roles: workOrder.page_roles || workOrder.extraction_data?.page_roles || {},
                      page_sizes: workOrder.page_sizes || workOrder.extraction_data?.page_sizes || []
                    }
                  }} />
                </pre>
              </div>
            )}

            {activeTab === "metadata" && (
              <div className="cob-metadata">
                <table className="cob-metadata-table">
                  <tbody>
                    <tr>
                      <td>Work Order ID</td>
                      <td>{workOrder._id}</td>
                    </tr>
                    <tr>
                      <td>Filename</td>
                      <td>{workOrder.filename || "-"}</td>
                    </tr>
                    <tr>
                      <td>UID</td>
                      <td>{workOrder.uid || "-"}</td>
                    </tr>
                    <tr>
                      <td>Status</td>
                      <td>{workOrder.status}</td>
                    </tr>
                    <tr>
                      <td>Total Pages</td>
                      <td>{workOrder.total_pages || "-"}</td>
                    </tr>
                    <tr>
                      <td>Cheques Found</td>
                      <td>{workOrder.cheques_found || cheques.length || 0}</td>
                    </tr>
                    <tr>
                      <td>Processing Time</td>
                      <td>{workOrder.processing_time ? `${parseFloat(workOrder.processing_time).toFixed(3)}s` : "-"}</td>
                    </tr>
                    <tr>
                      <td>Model</td>
                      <td>{workOrder.model || "Qwen VL"}</td>
                    </tr>
                    <tr>
                      <td>Created At</td>
                      <td>{formatDate(workOrder.created_at)}</td>
                    </tr>
                    <tr>
                      <td>Updated At</td>
                      <td>{formatDate(workOrder.updated_at)}</td>
                    </tr>
                    {/* Token Usage Section */}
                    {workOrder.tokens && (
                      <>
                        <tr className="cob-metadata-section">
                          <td colSpan={2}>Token Usage</td>
                        </tr>
                        <tr>
                          <td>Input Tokens</td>
                          <td>{workOrder.tokens.input?.toLocaleString() || workOrder.tokens.input_tokens?.toLocaleString() || "0"}</td>
                        </tr>
                        <tr>
                          <td>Output Tokens</td>
                          <td>{workOrder.tokens.output?.toLocaleString() || workOrder.tokens.output_tokens?.toLocaleString() || "0"}</td>
                        </tr>
                        <tr>
                          <td>Total Tokens</td>
                          <td>{workOrder.tokens.total?.toLocaleString() || workOrder.tokens.total_tokens?.toLocaleString() || "0"}</td>
                        </tr>
                      </>
                    )}
                    {/* Cost Section */}
                    {(workOrder.cost !== undefined && workOrder.cost !== null) && (
                      <>
                        <tr className="cob-metadata-section">
                          <td colSpan={2}>Cost Estimation</td>
                        </tr>
                        <tr>
                          <td>Estimated Cost</td>
                          <td>${typeof workOrder.cost === 'number' ? workOrder.cost.toFixed(6) : workOrder.cost}</td>
                        </tr>
                      </>
                    )}
                    {workOrder.api_log && (
                      <>
                        <tr className="cob-metadata-section">
                          <td colSpan={2}>API Request Details</td>
                        </tr>
                        <tr>
                          <td>Method</td>
                          <td>{workOrder.api_log.method || "-"}</td>
                        </tr>
                        <tr>
                          <td>Endpoint</td>
                          <td>{workOrder.api_log.endpoint || "-"}</td>
                        </tr>
                        <tr>
                          <td>IP</td>
                          <td>{workOrder.api_log.ip_address || "-"}</td>
                        </tr>
                        <tr>
                          <td>Status</td>
                          <td>
                            {workOrder.api_log.status || "-"}
                            {workOrder.api_log.status_code ? ` (${workOrder.api_log.status_code})` : ""}
                          </td>
                        </tr>
                        <tr>
                          <td>Response Time</td>
                          <td>
                            {workOrder.api_log.processing_time_seconds != null
                              ? `${parseFloat(workOrder.api_log.processing_time_seconds).toFixed(3)}s`
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <td>Created By</td>
                          <td>{workOrder.api_log.created_by || "-"}</td>
                        </tr>
                        <tr>
                          <td>Timestamp</td>
                          <td>{formatDate(workOrder.api_log.timestamp)}</td>
                        </tr>
                      </>
                    )}
                    {workOrder.error && (
                      <tr className="error-row">
                        <td>Error</td>
                        <td>{workOrder.error}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default COBWorkOrderDetail;
